import { AlertCircle, Pencil } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { WORDING } from "@/lib/openHouse/wording"
import { cn } from "@/lib/utils"

// Review & post body (new-flow-wording §1–4): one section per step with Edit
// back to it, the single terms checkbox, and the posting failure with a retry.
// Labels "Edit" and the fix-list heading are today's review screen's.
//
// Props: sections ([{ id, title, index, rows: [{ label, value }] }]), onEdit(index),
// terms, onTerms(bool), termsError, postFailed, onRetry, posting, issues ([message]),
// price (the guest page's price section, after the answers).
export function ReviewStep({
  sections, onEdit, terms, onTerms, termsError, postFailed, onRetry, posting, issues = [], price = null,
}) {
  return (
    <div className="flex flex-col gap-4">
      {postFailed && (
        <div role="alert" className="flex flex-wrap items-center gap-3 rounded-xl border border-destructive/30 bg-destructive/10 px-4 py-3 text-[13px] text-destructive">
          <AlertCircle className="size-4 shrink-0" strokeWidth={2.2} />
          <span className="min-w-0 flex-1">{WORDING.postFail}</span>
          <Button variant="outline" size="sm" onClick={onRetry} disabled={posting}>
            {WORDING.postRetry}
          </Button>
        </div>
      )}

      {sections.map((section) => (
        <div key={section.id} className="overflow-hidden rounded-xl border border-border">
          <div className="flex items-center justify-between gap-3 border-b border-border bg-accent px-4 py-2.5">
            <h3 className="font-display text-[13.5px] font-bold tracking-tight text-foreground">{section.title}</h3>
            <button
              type="button"
              onClick={() => onEdit(section.index)}
              className="flex shrink-0 items-center gap-1.5 text-[12px] font-semibold text-muted-foreground transition-colors hover:text-foreground"
            >
              <Pencil className="size-3.5" strokeWidth={2} />
              Edit
            </button>
          </div>
          {section.rows.map((row, i) => (
            <dl key={i} className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 border-b border-border px-4 py-2.5 last:border-b-0">
              <dt className="text-[12.5px] font-semibold text-foreground">{row.label}</dt>
              <dd className="min-w-0 break-words text-right text-[12.5px] text-muted-foreground">{row.value}</dd>
            </dl>
          ))}
        </div>
      ))}

      {price}

      {issues.length > 0 && (
        <div className="rounded-xl border border-destructive/30 bg-destructive/5 px-4 py-3">
          <p className="text-[12.5px] font-bold text-destructive">Please fix the following before posting:</p>
          <ul className="mt-1.5 flex flex-col gap-1.5">
            {issues.map((message, i) => (
              <li key={i} className="text-[12px] font-semibold text-destructive">{message}</li>
            ))}
          </ul>
        </div>
      )}

      <div data-post-field="terms" className="flex flex-col gap-2">
        <label
          className={cn(
            "flex cursor-pointer items-start gap-3 rounded-xl border px-4 py-3.5",
            termsError ? "border-destructive" : "border-line-2"
          )}
        >
          <Checkbox
            checked={terms}
            onCheckedChange={(v) => onTerms(v === true)}
            aria-invalid={!!termsError}
            className="mt-0.5"
          />
          <span className="text-[12.5px] leading-relaxed text-foreground">{WORDING.terms}</span>
        </label>
        {termsError && (
          <p role="alert" className="flex items-center gap-1.5 text-[12px] font-semibold text-destructive">
            <AlertCircle className="size-3.5" strokeWidth={2.2} />
            {WORDING.termsError}
          </p>
        )}
      </div>
    </div>
  )
}
